import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Plane, 
  Calendar, 
  Mail, 
  MessageSquare, 
  MessageCircle,
  LogOut,
  Menu,
  X,
  Image,
  Settings,
  FileText,
  Terminal,
  Server,
  ChevronDown,
  ChevronUp,
  Folder,
  MapPin,
  Bell,
  Star
} from 'lucide-react';

const LOGO_URL = "/logo%20viajes.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VERSION_API = `${process.env.REACT_APP_BACKEND_URL}/api/version`;

const NAV_ITEMS = [
  { path: '/gestion-agencia/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/gestion-agencia/viajes', icon: Plane, label: 'Viajes' },
  { path: '/gestion-agencia/destinos', icon: MapPin, label: 'Destinos' },
  { path: '/gestion-agencia/reservas', icon: Calendar, label: 'Reservas' },
  { path: '/gestion-agencia/calendario', icon: Calendar, label: 'Calendario' },
  { path: '/gestion-agencia/blog', icon: FileText, label: 'Blog' },
  { path: '/gestion-agencia/mensajes', icon: MessageSquare, label: 'Mensajes' },
  { path: '/gestion-agencia/chat', icon: MessageCircle, label: 'Chat en Vivo' },
  { path: '/gestion-agencia/newsletter', icon: Mail, label: 'Newsletter' },
  { path: '/gestion-agencia/testimonios', icon: Star, label: 'Testimonios' },
  { 
    label: 'Recursos',
    icon: Folder,
    submenu: [
      { path: '/gestion-agencia/media', icon: Image, label: 'Media' },
      { path: '/gestion-agencia/paginas', icon: FileText, label: 'Páginas' }
    ]
  },
  { 
    label: 'Configuración',
    icon: Settings,
    submenu: [
      { path: '/gestion-agencia/settings', icon: Settings, label: 'Ajustes' },
      { path: '/gestion-agencia/system', icon: Server, label: 'Estado Sistema' },
      { path: '/gestion-agencia/logs', icon: Terminal, label: 'Logs' }
    ]
  },
];

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [newMessages, setNewMessages] = useState(0);
  const [appVersion, setAppVersion] = useState('unknown');
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const locationRef = useRef(location);

  // Fetch app version on mount
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await axios.get(VERSION_API);
        setAppVersion(response.data.version);
      } catch (error) {
        console.warn('Failed to fetch version:', error);
      }
    };
    fetchVersion();
  }, []);

  // Initialize audio on first user interaction or mount
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3');
      audioRef.current.volume = 0.5;
      audioRef.current.preload = 'auto';
    }
  }, []);

  // Update location ref
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Sound for notifications
  const playNotificationSound = () => {
    if (!audioRef.current) return;
    
    // El navegador requiere una interacción previa. Intentamos reproducir.
    audioRef.current.currentTime = 0;
    const playPromise = audioRef.current.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.warn('Auto-play preventado por el navegador. Se requiere pulsar la campana.', error);
      });
    }
  };

  const showPushNotification = (title, options) => {
    if (!("Notification" in window)) {
      console.warn('Notifications not supported in this browser');
      return;
    }
    
    if (Notification.permission === "granted") {
      try {
        new Notification(title, options);
      } catch (e) {
        console.error('Error creating notification:', e);
      }
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          new Notification(title, options);
        }
      });
    }
  };

  const testNotifications = () => {
    playNotificationSound();
    showPushNotification("Test de Notificación", {
      body: "Si ves esto, las notificaciones están funcionando perfectamente.",
      icon: LOGO_URL
    });
    toast.success("Prueba de sonido enviada");
  };

  useEffect(() => {
    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/gestion-agencia');
      return;
    }
    
    setUser(JSON.parse(userData));

    const fetchUnreadCount = async () => {
      /* Chat deshabilitado temporalmente */
    };

    // fetchUnreadCount();
    // const countInterval = setInterval(fetchUnreadCount, 60000);

    // Connect to global chat monitor
    if (false && !socketRef.current) {
      const getWsBase = () => {
        if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'RELATIVE') {
          return process.env.REACT_APP_BACKEND_URL.replace('http', 'ws');
        }
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}`;
      };

      const connectWS = () => {
        const WS_BASE = getWsBase();
        const ws = new WebSocket(`${WS_BASE}/ws/admin/chat`);
        
        ws.onopen = () => console.log('🔔 Admin Monitor conectado');
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 [AdminLayout WS] Mensaje:', data.type, data.session_id);

            // Emitimos evento global con prioridad y logs claros
            console.log('📤 [AdminLayout] Despachando admin-chat-event para:', data.type);
            const chatEvent = new CustomEvent('admin-chat-event', { 
              detail: data,
              bubbles: true,
              cancelable: true,
              composed: true 
            });
            window.dispatchEvent(chatEvent);
            
            if (data.type === 'new_message' || data.type === 'user_online') {
              const isFromUser = data.type === 'user_online' || (data.message && data.message.sender === 'user');
              
              if (isFromUser) {
                playNotificationSound();
                
                const isChatPage = locationRef.current.pathname.includes('/chat');
                
                // Notificaciones visuales (No aparecerán si ya estamos en la página del chat)
                if (!isChatPage) {
                  setNewMessages(prev => prev + 1);

                  const title = data.type === 'user_online' 
                    ? `🟢 Nuevo usuario: ${data.name || 'Cliente'}` 
                    : `💬 Mensaje de ${data.message.name || 'Cliente'}`;
                  
                  const body = data.type === 'user_online'
                    ? 'Ha entrado al chat y está esperando.'
                    : data.message.message;

                  showPushNotification(title, {
                    body,
                    icon: LOGO_URL,
                    tag: data.session_id,
                    requireInteraction: true
                  });

                  toast(title, {
                    description: body,
                    action: {
                      label: 'Atender',
                      onClick: () => navigate('/gestion-agencia/chat')
                    },
                  });
                }
              }
            }
          } catch (err) {
            console.error('Error procesando mensaje WS Global:', err);
          }
        };

        ws.onclose = () => {
          console.log('🔔 Admin Monitor desconectado, reintentando...');
          socketRef.current = null;
          setTimeout(connectWS, 5000);
        };

        ws.onerror = (err) => {
          console.error('🔔 WebSocket error:', err);
        };

        socketRef.current = ws;
      };

      connectWS();
    }

    // Auto-expand menus if current path is in submenu
    const newExpanded = {};
    NAV_ITEMS.forEach((item) => {
      if (item.submenu) {
        const isActive = item.submenu.some(sub => location.pathname === sub.path);
        if (isActive) {
          newExpanded[item.label] = true;
        }
      }
    });
    setExpandedMenus(newExpanded);

    return () => {
      // We keep the socket alive across navigations unless we unmount the whole layout or logout
    };
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (location.pathname.includes('/chat')) {
      setNewMessages(0);
    }
  }, [location.pathname]);

  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/gestion-agencia');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-primary h-16 flex items-center justify-between px-4 z-50">
        <img src={LOGO_URL} alt="Logo" className="h-10" />
        <div className="flex items-center gap-4">
          {newMessages > 0 && (
            <Link to="/gestion-agencia/chat" className="relative text-white">
              <MessageCircle className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 bg-accent text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-primary">
                {newMessages}
              </span>
            </Link>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-primary z-40 transform transition-transform duration-300
          lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}
        data-testid="admin-sidebar"
      >
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          <div className="flex items-center justify-between mb-8">
            <img src={LOGO_URL} alt="Viajes de Ensueño" className="h-16" />
            <div className="flex flex-col items-center">
              <button 
                onClick={testNotifications}
                className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Probar sonido y notificaciones"
              >
                <Bell className="w-5 h-5" />
              </button>
              <span className="text-[9px] font-mono text-white/30 mt-1 uppercase tracking-tighter">
                v2.0.0
              </span>
            </div>
          </div>
          
          <nav className="space-y-2 pb-20">
            {NAV_ITEMS.map((item) => (
              item.submenu ? (
                // Menu with submenu
                <div key={item.label} className="space-y-1">
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className="admin-nav-link w-full justify-between"
                  >
                    <span className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </span>
                    {expandedMenus[item.label] ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {expandedMenus[item.label] && (
                    <div className="ml-4 space-y-1">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setSidebarOpen(false)}
                          data-testid={`admin-nav-${subItem.label.toLowerCase()}`}
                          className={`admin-nav-link text-sm ${location.pathname === subItem.path ? 'active' : ''}`}
                        >
                          <subItem.icon className="w-4 h-4" />
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Regular menu item
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  className={`admin-nav-link justify-between ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </span>
                  {item.path === '/gestion-agencia/chat' && newMessages > 0 && (
                    <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg shadow-red-500/20">
                      {newMessages}
                    </span>
                  )}
                </Link>
              )
            ))}
          </nav>
        </div>

        <div className="p-6 border-t border-white/10 bg-primary mt-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white font-semibold">
              {user.username?.charAt(0).toUpperCase()}
            </div>
            <div className="text-white">
              <p className="font-medium">{user.username}</p>
              <p className="text-xs text-white/60">Administrador</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>

          <Link
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mt-4"
          >
            Ver web pública →
          </Link>
          
          <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-[10px] text-white/40 uppercase tracking-widest font-mono">
             <span>Plataforma v{appVersion}</span>
             <span className="text-emerald-400 capitalize">● Online</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
